// components/ChatHeader.tsx
import React from 'react';
import { useClipShare } from '../hooks/useClipShare';
import { useLanguage } from '../context/LanguageContext';

interface ChatHeaderProps {
  sessionCode: string;
  userNickname: string;
  connectionStatus: 'connected' | 'disconnected' | 'connecting';
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({ 
  sessionCode, 
  userNickname, 
  connectionStatus 
}) => {
  const { participants, leaveSession, userId } = useClipShare();
  const { t } = useLanguage();

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return t.connected;
      case 'connecting': return t.connecting;
      case 'disconnected': return t.disconnected;
      default: return t.unknownStatus;
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'var(--success)';
      case 'connecting': return 'var(--warning)';
      case 'disconnected': return 'var(--error)';
      default: return 'var(--text-secondary)';
    }
  };

  const getParticipantText = () => {
    const count = participants.size;
    if (count === 1) {
      return t.participant;
    }
    return t.participants;
  };

  return (
    <div className="chat-header">
      <div className="chat-info">
        <div 
          className="status-indicator" 
          style={{ background: getStatusColor() }}
        />
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {t.session} {sessionCode}
          </div>
          <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            {getStatusText()} • {userNickname} • {participants.size} {getParticipantText()}
          </div>
        </div>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <div className="participants">
          {Array.from(participants.values()).map((participant) => (
            <div
              key={participant.id}
              className="participant"
              title={`${participant.nickname} (${participant.id.slice(0, 8)})${participant.id === userId ? ` - ${t.you}` : ''}`}
              style={participant.id === userId ? {
                background: 'linear-gradient(135deg, var(--accent), var(--primary-color))'
              } : {}}
            >
              {participant.nickname.charAt(0).toUpperCase()}
            </div>
          ))}
        </div>
        
        <button 
          className="btn btn-secondary" 
          onClick={leaveSession}
        >
          {t.leave}
        </button>
      </div>
    </div>
  );
};